import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ReceivingAdminService } from '../../../services/receiving-admin.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { ShareddtaService } from '@app/services/shareddta.service';
import { AuthServiceService } from '@app/services/auth-service.service';
import { ToastService } from '@app/services/toast.service';
import { ReusableGoogleMapComponent } from '@app/components/reusable-google-map/reusable-google-map.component';
import { MatDialog } from '@angular/material/dialog';
import { BreadcrumbService } from '@app/services/breadcrumb.service';

@Component({
    selector: 'app-receiving-order-detail',
    templateUrl: './receiving-order-detail.component.html',
    styleUrls: ['./receiving-order-detail.component.scss']
})
export class ReceivingOrderDetailComponent implements OnInit, AfterViewInit, OnDestroy {
    orderId: string | null = null;
    schema: string | null = null;
    orderData: any = null;
    isLoading: boolean = false;
    orderStatus: string = '';
    truckPosition: number = 0;
    statusSteps: any[] = [];
    driverAssigned: boolean = false;
    notificationRecipient: 'driver' | 'customer' = 'customer';
    uploadedImageUrl: string | null = null;
    mapEmbedUrl: SafeResourceUrl | null = null;

    statusFlow = [
        { key: 'pending', label: 'PROCESSED', icon: 'clipboard-check' },
        { key: 'shipped', label: 'SHIPPED', icon: 'cube' },
        { key: 'en-route', label: 'EN ROUTE', icon: 'truck' },
        { key: 'arrived', label: 'ARRIVED', icon: 'map-pin' },
        { key: 'successful', label: 'DELIVERED', icon: 'check-circle' },
        { key: 'unsuccessful', label: 'UNSUCCESSFUL', icon: 'check-circle' },
        { key: 'cancelled', label: 'CANCELLED', icon: 'check-circle' }
    ];

    private destroy$ = new Subject<void>();

    constructor(
        private route: ActivatedRoute,
        public router: Router,
        private receivingService: ReceivingAdminService,
        private shareddta: ShareddtaService,
        private authService: AuthServiceService,
        private toast: ToastService,
        private dialog: MatDialog,
        private breadcrumbService: BreadcrumbService,
        private sanitizer: DomSanitizer
    ) { }

    ngOnInit(): void {
        this.breadcrumbService.set([
            { label: 'Orders', action: () => this.goBack() },
            { label: 'Order Details' }
        ]);

        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            this.orderId = params['id'];
            this.schema = this.route.snapshot.queryParams['schema'];
            if (this.orderId && this.schema) {
                this.fetchOrderDetail();
            }
        });

        this.receivingService.loading$.pipe(takeUntil(this.destroy$)).subscribe(loading => {
            this.isLoading = loading;
        });
    }

    ngAfterViewInit(): void {
        this.shareddta.signalPageReady();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getOrderAddress(): any {
        if (!this.orderData) return null;
        const isParentNull = this.orderData.parent_order == null;
        if (isParentNull) {
            return this.orderData.order_type === 'delivery' 
                ? this.orderData.dely_address 
                : this.orderData.pickup_address;
        } else {
            return this.orderData.dely_address;
        }
    }

    updateMapEmbedUrl(): void {
        const addressObj = this.getOrderAddress();
        const lat = addressObj?.latitude;
        const lng = addressObj?.longitude;
        const addressStr = addressObj?.address || this.orderData?.customer_addr;

        let url = '';
        if (lat && lng && !isNaN(Number(lat)) && !isNaN(Number(lng)) && Number(lat) !== 0 && Number(lng) !== 0) {
            url = `https://maps.google.com/maps?q=${lat},${lng}&z=15&output=embed`;
        } else if (addressStr) {
            url = `https://maps.google.com/maps?q=${encodeURIComponent(addressStr)}&z=15&output=embed`;
        }
        this.mapEmbedUrl = url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
    }

    fetchOrderDetail(): void {
        if (!this.orderId || !this.schema) return;

        this.receivingService.getReceivingOrderDetail(this.orderId, this.schema)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    if (this.authService.resultCodeError(response)) {
                        return;
                    }
                    this.orderData = response?.result || response;
                    this.orderStatus = (this.orderData?.order_status || '').toLowerCase();
                    this.updateMapEmbedUrl();

                    if (this.orderData?.current_driver && Object.keys(this.orderData.current_driver).length > 0) {
                        this.driverAssigned = true;
                    } else {
                        this.driverAssigned = false;
                    }

                    if (this.orderData?.order_invoice?.length > 0) {
                        this.uploadedImageUrl = this.orderData.order_invoice[this.orderData.order_invoice.length - 1].invoice_doc;
                    }

                    this.buildStatusStepsFromApi(this.orderData);

                    // Update dynamic breadcrumb
                    const orderLabel = this.orderData.parent_order == null 
                        ? (this.orderData.order_type === 'pickup' ? this.orderData.po : this.orderData.order_no)
                        : this.orderData.order_no;
                    
                    const finalLabel = orderLabel || `Order #${this.orderId}`;
                    
                    this.breadcrumbService.set([
                        { label: 'Orders', action: () => this.goBack() },
                        { label: finalLabel }
                    ]);
                },
                error: (error) => {
                    this.authService.dataError(error);
                }
            });
    }

    buildStatusStepsFromApi(orderData: any) {
        const history = orderData?.status_history || [];
        if (!history.length) return;

        const terminalStatuses = ['successful', 'unsuccessful', 'cancelled'];
        const historyMap = history.reduce((acc: any, h: any) => {
            acc[h.status] = h;
            return acc;
        }, {});

        const terminalInHistory = terminalStatuses.find(ts => !!historyMap[ts]);

        this.statusSteps = this.statusFlow
            .filter(step => {
                if (terminalStatuses.includes(step.key)) {
                    return step.key === terminalInHistory;
                }
                return true;
            })
            .map(step => {
                const historyEntry = historyMap[step.key];
                const isCompleted = !!historyEntry;
                const isActive = !!historyEntry;

                return {
                    ...step,
                    completed: isCompleted,
                    active: isActive,
                    isCurrent: false,
                    time: historyEntry
                        ? this.formatDateTime(historyEntry.created_at)
                        : isActive
                            ? 'In progress'
                            : ''
                };
            });

        const lastActiveIndex = [...this.statusSteps].reverse().findIndex(s => s.active);
        if (lastActiveIndex !== -1) {
            const realIndex = this.statusSteps.length - 1 - lastActiveIndex;
            this.statusSteps[realIndex].isCurrent = true;
        }

        this.animateStatusProgress();
    }

    animateStatusProgress(): void {
        const totalSteps = this.statusSteps.length;
        const completedSteps = this.statusSteps.filter(s => s.completed).length;
        const progressPercent = totalSteps > 1 ? (completedSteps / totalSteps) * 100 : 0;

        setTimeout(() => {
            this.truckPosition = Math.max(progressPercent, 0);
            const progressLine = document.querySelector('.status-line-progress') as HTMLElement;
            if (progressLine) {
                progressLine.style.width = `${this.truckPosition}%`;
            }
        }, 300);
    }

    formatDateTime(dateStr: string | Date): string {
        const date = new Date(dateStr);
        const datePart = date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
        const timePart = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        return `${datePart}\n${timePart}`;
    }

    convertToLocalDate(utcDateString: string | null): string {
        if (!utcDateString) return '';
        const datePipe = new DatePipe('en-US');
        const utcDate = new Date(utcDateString);
        return datePipe.transform(utcDate, 'EEE MMM dd yyyy') || '';
    }

    getFormattedTime(): string {
        if (this.orderData && this.orderData.dely_time) {
            const timeParts = this.orderData.dely_time.split(':');
            let hours = parseInt(timeParts[0], 10);
            const minutes = timeParts[1];
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            return `${hours}:${minutes} ${ampm}`;
        }
        return '';
    }

    getFormattedTime1(): string {
        if (this.orderData && this.orderData.pickup_time) {
            const timeParts = this.orderData.pickup_time.split(':');
            let hours = parseInt(timeParts[0], 10);
            const minutes = timeParts[1];
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            return `${hours}:${minutes} ${ampm}`;
        }
        return '';
    }

    getMultipleOrderIds(orderData: any): string {
        if (orderData?.multiple_order) {
            return orderData?.order_type === 'delivery' ?
                (orderData?.dely_multiple_order_ids || 'N/A') :
                (orderData?.pickup_multiple_order_ids || 'N/A');
        }
        return 'N/A';
    }

    getDriverInitials(driver: any): string {
        if (!driver || !driver.first_name || !driver.last_name) return '';
        return driver.first_name.charAt(0).toUpperCase() + driver.last_name.charAt(0).toUpperCase();
    }

    trackOrder(): void {
        if (this.orderStatus === 'en-route' || this.orderStatus === 'arrived') {
            console.log('Tracking order:', this.orderId);
            this.trackOrders();
        }
    }
     dialogRef: any;
      trackOrders() {
        console.log('this.orderdata', this.orderData)
        //  this.myFunction(this.title,'trackDriver');
        let enterAnimationDuration = '200ms';
        let exitAnimationDuration = '200ms';
        this.dialogRef = this.dialog.open(ReusableGoogleMapComponent, {
          width: '1100px',
          height: '90vh',
          enterAnimationDuration,
          exitAnimationDuration,
          data: {
            height: '100%',
            isShowDirections: true,
            orderData: this.orderData
          },
        });
        this.dialogRef.afterClosed().subscribe((dialogResult: any) => {
          if (dialogResult) {
    
          }
        });
    
      }

    goBack(): void {
        this.router.navigate(['/receiving-admin/orders']);
    }

    getStatusClass(status: string): string {
        const s = status?.toLowerCase();
        if (s === 'pending') return 'pending';
        if (s === 'successful' || s === 'delivered' || s === 'completed' || s === 'shipped') return 'successful';
        if (s === 'unsuccessful' || s === 'failed') return 'unsuccessful';
        if (s === 'cancelled') return 'cancelled';
        if (s === 'en-route' || s === 'in-transit') return 'en-route';
        if (s === 'arrived') return 'arrived';
        if (s === 'warehouse') return 'warehouse';
        return '';
    }

    getStatusBadgeClass(): string {
        switch (this.orderStatus) {
            case 'pending': return 'pending';
            case 'unsuccessful': return 'unsuccessful';
            case 'successful': return 'successful';
            case 'en-route': return 'en-route';
            case 'shipped': return 'shipped';
            case 'warehouse': return 'warehouse';
            case 'arrived': return 'arrived';
            case 'cancelled': return 'cancelled';
            default: return 'pending';
        }
    }

    getStatusBadgeText(): string {
        return (this.orderStatus || '').toUpperCase();
    }

    copyToClipboard(text: string): void {
        if (!text) return;
        navigator.clipboard.writeText(text).then(() => {
            this.toast.show('success', 'Copied!');
        });
    }

    toggleNotificationMode(mode: 'driver' | 'customer'): void {
        this.notificationRecipient = mode;
    }

    // Placeholder actions
    // openNotifications(): void { console.log('Open notifications'); }
    editOrder(data: any): void { console.log('Edit order', data); }
    deleteOrder(): void { console.log('Delete order'); }
    uploadPackingSlip(): void { console.log('Upload packing slip'); }
    handleFileUpload(event: any): void { console.log('Handle file upload', event); }
    clearUpload(): void { console.log('Clear upload'); }
    openModal(): void { console.log('Open notification modal'); }
    openSignatureModal(img: string): void { console.log('Open signature modal', img); }
    unassignDriver(): void { console.log('Unassign driver'); }
    rescheduleOrder(): void { console.log('Reschedule order'); }
    openReAssignOrder(): void { console.log('Open reassign order'); }
orderDetailRn:any;
activeTab:any
orderDetailRnD:any;
      setActiveTab(tab: 'customer' | 'driver'): void {
        this.activeTab = 'customer'
    this.receivingService.recivingorderDetailRNC( this.orderId, this.schema).subscribe(
      (data: any) => {
        // 🔹 Check API response error
        if (this.authService.resultCodeError(data)) {
          return;
        }

       this.orderDetailRn = data?.result?.results || [];
        console.log('Order Notifications:', this.orderDetailRn);
      },
      (error: any) => {
        this.authService.dataError(error);
      }
    );
  }
    viewAllNotifications(): void {
    this.orderDetailRnD = [];
    console.log('View all notifications for:', this.activeTab);
     this.allSetActiveTab('customer');
    // this.setActiveTabAll(this.activeTab);
    // Navigate to full notifications page
  }
  isNotificationPanelOpen:any;
   closeNotifications(): void {
    this.isNotificationPanelOpen = false;
  }
    openNotifications(): void {
    this.isNotificationPanelOpen = true;
    this.orderDetailRnD = [];
    this.setActiveTab('customer');
  }
      allSetActiveTab(tab: 'customer' | 'driver'): void {
        this.activeTab = 'customer'
    this.receivingService.recivingorderDetailAllRNC( this.orderId, this.schema).subscribe(
      (data: any) => {
        // 🔹 Check API response error
        if (this.authService.resultCodeError(data)) {
          return;
        }

       this.orderDetailRn = data?.result?.results || [];
        console.log('Order Notifications:', this.orderDetailRn);
      },
      (error: any) => {
        this.authService.dataError(error);
      }
    );
  }
}
